import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ComponentConditionKey, GraderCosmeticComponent, graderComponents } from "@pandas/assessment-engine";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck } from "tabler-icons-react";
import deviceScanAnimation from "@/assets/device-scan-animation.json";
import LoadingDots from "@/components/LoadingDots";
import { getExpectedAngles } from "@/components/phone-capture/capture-wizard/flows/angleFlowFactory";
import { getCaptureFlowConfig } from "@/components/phone-capture/capture-wizard/flows/captureFlowFactory";
import { servicesOrchestratorValues } from "@/config/services";
import { GradingError } from "@/hooks/useGradingErrors";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { GraderChannel } from "@/lib/ably/vision";
import { assessmentEngine } from "@/lib/assessments/assessmentEngineSingleton";
import logger from "@/lib/logging/logger";
import { graderApi } from "@/services";
import { type GradingOutcome, hasAlerts, outcomeFromAlerts } from "@/services/grader/outcome";
import { waitForCompletePhotoSet } from "@/services/grader/photoSet";
import { useStore } from "@/store";
import { delay } from "@/utils/helpers";

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/device-grading")({
	component: DeviceGrading,
});

const isGraderCosmeticComponent = (questionId: string): questionId is GraderCosmeticComponent =>
	graderComponents.includes(questionId as GraderCosmeticComponent);

function DeviceGrading() {
	const { t } = useTranslation();
	const setError = useStore((state) => state.setError);
	const clearError = useStore((state) => state.clearError);
	const timer = useStore((state) => state.timer);
	const clearTimer = useStore((state) => state.clearTimer);
	const setTimer = useStore((state) => state.setTimer);
	const photos = useStore((state) => state.photos);
	const sessionId = useStore((state) => state.sessionId) as string;
	const partnerCode = useStore((state) => state.instanceInfo?.partner.code) as string;
	const imeiInfo = useStore((state) => state.imeiInfo);
	const location = useStore((state) => state.instanceInfo?.location);
	const gradeRequestId = useStore((state) => state.gradeRequestId);
	const setGradeRequestId = useStore((state) => state.setGradeRequestId);
	const componentVersion = useStore((state) => state.config?.partnerSettings.componentVersion);
	const captureFlowId =
		useStore((state) => state.config?.pagesConfig.imageCapture?.captureFlow) || "BACK_CAMERA_MANUAL";
	const configuredAngles = useStore((state) => state.config?.pagesConfig.imageCapture?.captureAngles) || [];
	const [checkedComponents, setCheckedComponents] = useState<Record<string, boolean>>({});
	const refCheckedComponents = useRef<Record<string, boolean>>({});
	const components = useMemo(() => {
		const graderAssessments = assessmentEngine.GetBatchByKeysSorted([...graderComponents]);
		return graderAssessments.map((c) => c.ComponentName);
	}, []);
	const deviceInfo = imeiInfo?.identification.extracted_metadata;
	const { goToNextStep } = useNavigationHelpers();

	const inflightGradeRequest = useRef(false);
	const handledGradeRequestRef = useRef<string | null>(null);
	const pausedTimerRef = useRef<{ totalTimeout: number; popupTimeout: number } | null>(null);

	const isCurrentGradeRequest = useCallback(() => {
		const state = useStore.getState();
		return Boolean(gradeRequestId && state.gradeRequestId === gradeRequestId && state.sessionId === sessionId);
	}, [gradeRequestId, sessionId]);

	const progressiveComponentCheck = useCallback(
		async (length: number, delayOffset: number) => {
			for (let i = 0; i < length; i++) {
				const remaining = components.filter((c) => !refCheckedComponents.current[c]);
				if (remaining.length === 0) break;

				const randomInterval = Math.floor(Math.random() * 1000) + delayOffset;
				await delay(randomInterval);
				const randomComponent = remaining[Math.floor(Math.random() * remaining.length)]!;
				setCheckedComponents((prev) => ({ ...prev, [randomComponent]: true }));
				refCheckedComponents.current = { ...refCheckedComponents.current, [randomComponent]: true };
			}
		},
		[components],
	);

	const processGradeResults = useCallback(
		async (outcome: GradingOutcome) => {
			if (!gradeRequestId || handledGradeRequestRef.current === gradeRequestId) {
				logger.info("INFO", "Ignoring stale or duplicate grading results", { gradeRequestId });
				return;
			}

			if (!isCurrentGradeRequest()) {
				logger.info("INFO", "Ignoring grading results for inactive session", { gradeRequestId, sessionId });
				return;
			}

			handledGradeRequestRef.current = gradeRequestId;
			clearError();

			try {
				if (hasAlerts(outcome)) {
					logger.warning("INFO", "Grading error", outcome);
					setGradeRequestId(null);
					setError(new GradingError(outcome));
					return;
				}

				if (pausedTimerRef.current) {
					setTimer(pausedTimerRef.current);
				}

				for (const [questionId, response] of Object.entries(outcome.responses)) {
					if (questionId === "screenProtector") continue;
					if (!isGraderCosmeticComponent(questionId)) {
						logger.warning("INFO", "Ignoring unsupported grading response", { questionId });
						continue;
					}
					const assessment = assessmentEngine.GetByKey(questionId);
					assessment.SetConditionByKey(response as ComponentConditionKey);
				}
				await assessmentEngine.AddAssessmentsToPlatform(sessionId);
				await progressiveComponentCheck(components.length, 0);

				await delay(500);
				goToNextStep();
			} catch (error) {
				handledGradeRequestRef.current = null;
				if (!isCurrentGradeRequest()) {
					logger.info("INFO", "Ignoring grading result error for inactive session", error);
					return;
				}
				logger.error("INFO", "Something went wrong handling Grading results", error);
				setGradeRequestId(null);
				setError(error as Error);
			}
		},
		[
			clearError,
			components.length,
			goToNextStep,
			gradeRequestId,
			isCurrentGradeRequest,
			progressiveComponentCheck,
			sessionId,
			setError,
			setGradeRequestId,
			setTimer,
		],
	);

	// A grader that never answers ends like an unreadable photo set: the designed retake
	// popup for every expected angle. The in-flight guard keeps the effect below from
	// resubmitting on its own once the request id is cleared.
	const handleGraderGiveUp = useCallback(() => {
		if (!gradeRequestId || !isCurrentGradeRequest()) return;
		logger.error("FLOW", "The grader did not answer within the polling budget", { gradeRequestId, sessionId });
		handledGradeRequestRef.current = gradeRequestId;
		const { angleSequence } = getCaptureFlowConfig(captureFlowId);
		const alerts = Object.fromEntries(
			getExpectedAngles(angleSequence, configuredAngles).map((angle) => [angle, ["unexpected-issues"]]),
		);
		setGradeRequestId(null);
		setError(new GradingError(outcomeFromAlerts(alerts)));
	}, [captureFlowId, configuredAngles, gradeRequestId, isCurrentGradeRequest, sessionId, setError, setGradeRequestId]);

	useEffect(() => {
		if (!timer) return;

		pausedTimerRef.current = {
			totalTimeout: timer.totalTimeout,
			popupTimeout: timer.popupTimeout,
		};
		clearTimer();
	}, [clearTimer, timer]);

	useEffect(() => {
		// The photo-set wait polls for up to its full deadline; without this it resolves
		// into a grade request for a screen that is gone.
		const abortController = new AbortController();

		const initiateGradingProcess = async () => {
			// Prevent duplicate grade requests if one was already submitted for this session
			if (gradeRequestId) {
				logger.info("INFO", `Grade request already exists with id ${gradeRequestId}, skipping duplicate request`);
				return;
			}
			// StrictMode re-runs this effect synchronously before the POST resolves,
			// so the gradeRequestId state guard above can pass twice. A ref survives
			// the double-mount and blocks the in-flight second call.
			if (inflightGradeRequest.current) return;
			inflightGradeRequest.current = true;

			const { angleSequence } = getCaptureFlowConfig(captureFlowId);
			const expectedAngles = getExpectedAngles(angleSequence, configuredAngles);
			const { photos: completePhotos, missingAngles } = await waitForCompletePhotoSet(expectedAngles, {
				initialPhotos: photos,
				getPhotos: () => useStore.getState().photos,
				signal: abortController.signal,
			});
			if (abortController.signal.aborted) {
				inflightGradeRequest.current = false;
				return;
			}
			if (!completePhotos || missingAngles.length > 0) {
				logger.error("INFO", "Grade request aborted: photo set incomplete", {
					expected: expectedAngles,
					received: Object.keys(completePhotos ?? {}),
					missing: missingAngles,
				});
				const alerts = Object.fromEntries(missingAngles.map((angle) => [angle, ["unexpected-issues"]]));
				inflightGradeRequest.current = false;
				setError(new GradingError(outcomeFromAlerts(alerts)));
				return;
			}

			try {
				const resp = await graderApi.submitRemoteGrading({
					source: "vision",
					store: location?.name,
					device: deviceInfo ? `${deviceInfo.description}` : " ",
					subject_identifier: deviceInfo?.identifier,
					session_id: sessionId,
					images: Object.values(completePhotos),
					partner_code: partnerCode,
					location_id: location?.id,
					component_version: componentVersion || "3",
				});
				setGradeRequestId(resp.id);
				await progressiveComponentCheck(components.length - 1, 2000);
			} catch (error) {
				inflightGradeRequest.current = false;
				setGradeRequestId(null);
				setError(error as Error);
				logger.error("INFO", "Could not create grade request", error);
			}
		};
		initiateGradingProcess();

		return () => {
			abortController.abort();
		};
	}, [
		captureFlowId,
		configuredAngles,
		componentVersion,
		deviceInfo,
		gradeRequestId,
		location?.id,
		location?.name,
		partnerCode,
		photos,
		components.length,
		progressiveComponentCheck,
		setError,
		setGradeRequestId,
	]);

	return (
		<>
			{gradeRequestId && (
				<GraderChannel
					channelId={gradeRequestId}
					cb={processGradeResults}
					onGiveUp={handleGraderGiveUp}
				/>
			)}
			<div className="flex flex-col items-center justify-between h-full w-full">
				<div className="w-full h-[30%] sm:h-[40%] max-w-[700px] relative m-4 items-center flex justify-center">
					<DotLottieReact
						data={deviceScanAnimation}
						loop
						autoplay
						className="h-full aspect-[328/208]"
					/>
				</div>
				<div className="w-full overflow-auto h-[60%] relative sm:p-6">
					<div className="flex-1 flex flex-col items-center justify-center w-full h-fit gap-2">
						{components.map((component) => (
							<div
								className="flex py-3 pr-6 pl-5 rounded-lg justify-between items-center bg-lightgrey w-full max-w-[628px] gap-5"
								key={component}
							>
								<div className="flex items-center gap-5">
									<div className="w-10 h-10 sm:w-14 sm:h-14 relative flex shrink-0">
										<img
											src={`${servicesOrchestratorValues.urls.cdn}/kiosk/assessments/outlines/${component}.svg`}
											alt={component}
											className="w-full h-full object-contain"
										/>
									</div>
									<h4 className="font-medium">{t(`report.${component}`)}</h4>
								</div>
								<div className="flex-shrink-0 flex-nowrap">
									{checkedComponents[component] ? <CircleCheck size={32} /> : <LoadingDots />}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</>
	);
}
